import { useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';

import {
  LanguageType,
  ProductReviewsQuery
} from '@/__generated__/gateway/graphql';
import { productReviewsQuery } from '@/shared/composables/productReviews/productReviewsGateway.queries';
import { REVIEWS_PER_PAGE } from '@/shared/constants/product';
import { getLanguageType } from '@/shared/utils/gateway/helpers';
import { NestedTypeByPath } from '@/shared/utils/typescript';

export type ProductReview = NestedTypeByPath<
  ProductReviewsQuery,
  ['productReviews', 'listReviews', 'reviews', number]
>;

export const useProductReviews = (productId: string) => {
  const retrying = ref(false);
  const loadingMore = ref(false);

  const queryVariables = {
    input: {
      product_id: productId,
      language: getLanguageType(window.Shopify.locale, LanguageType.En),
      limit: REVIEWS_PER_PAGE
    }
  };

  const { loading, result, error, refetch, onResult, onError, fetchMore } =
    useQuery(productReviewsQuery, queryVariables, { clientId: 'gateway' });

  const reviews = computed(
    () => result.value?.productReviews?.listReviews?.reviews ?? null
  );
  const loadMoreCursor = computed(
    () => result.value?.productReviews?.listReviews?.cursor
  );
  const hasMore = computed(() => !!loadMoreCursor.value);

  const loadMore = async () => {
    if (loadMoreCursor.value) {
      loadingMore.value = true;
      try {
        await fetchMore({
          variables: {
            input: {
              ...queryVariables.input,
              cursor: loadMoreCursor.value
            }
          },
          updateQuery: appendToExisting
        });
      } finally {
        loadingMore.value = false;
      }
    }
  };
  const retry = () => {
    retrying.value = true;
    refetch();
  };
  onResult(() => {
    if (retrying.value) {
      retrying.value = false;
    }
  });
  onError(() => {
    if (retrying.value) {
      retrying.value = false;
    }
  });

  return {
    loading,
    reviews,
    error,
    retry,
    retrying,
    hasMore,
    loadMore,
    loadingMore
  };
};

const appendToExisting = (
  previousResult: ProductReviewsQuery,
  { fetchMoreResult }: { fetchMoreResult?: ProductReviewsQuery }
): ProductReviewsQuery => {
  if (
    !previousResult?.productReviews?.listReviews?.reviews ||
    !fetchMoreResult?.productReviews?.listReviews?.reviews
  ) {
    return previousResult;
  }

  return {
    ...fetchMoreResult,
    productReviews: {
      ...fetchMoreResult.productReviews,
      listReviews: {
        ...fetchMoreResult.productReviews.listReviews,
        reviews: [
          ...previousResult.productReviews.listReviews.reviews,
          ...fetchMoreResult.productReviews.listReviews.reviews
        ]
      }
    }
  };
};
